import {
  type ColDef,
  type DefaultMenuItem,
  type GetContextMenuItemsParams,
  type IDetailCellRendererParams,
  type IRowNode,
  type MenuItemDef,
} from "ag-grid-community"
import { type AgGridReact } from "ag-grid-react"
import { ArrowsIn, ArrowsOutSimple } from "@phosphor-icons/react"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import { useEventCallback } from "usehooks-ts"

import { trackEvent } from "~/hooks/useTrackingService"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { useAllStockOutTimes } from "~/hooks/useVariantTableHooks"
import { collapseMasterRows } from "~/utils/ag-utils"
import { api, type RouterOutputs } from "~/utils/api"
import { getSoldAndActiveSince } from "~/utils/datetime"
import { type Filter } from "~/utils/filter"
import { getForecastsForWeeks } from "~/utils/forecastUtils"
import { readLeadTimeAvailability } from "~/utils/stockProjection"
import { filterDefined, sumBy, uniq } from "~/utils/tsUtils"

import { PRODUCT_TABLE_AGG_FUNCS } from "./utils/columnVisibility"
import { menuIcon } from "./utils/menuIcon"

// Matches the zod cap on sku.getSkuDataForVariants
const EXPAND_ALL_SKU_MAX_ROWS = 500

type SkuMenuItem = DefaultMenuItem | MenuItemDef<IVariantSummary>

export function useSkuMasterDetail({
  gridRef,
  masterDetail,
  canExpandAllSku,
  warehouseId,
  channel,
  defaultColumnDef,
  buildDetailColumnDefs,
  searchText,
  searchBy,
  localFilters,
}: {
  gridRef: React.RefObject<AgGridReact<IVariantSummary> | null>
  masterDetail: boolean | undefined
  canExpandAllSku: boolean
  warehouseId: string | undefined
  channel: string | undefined
  defaultColumnDef: ColDef<IVariantSummary>
  buildDetailColumnDefs: () => ColDef<any>[]
  searchText: string
  searchBy: string
  localFilters: Filter[]
}) {
  const trpcUtils = api.useUtils()
  // Lead-time availability ships beside the stockout results, not attached to them.
  const { data: allStockOutTimes } = useAllStockOutTimes()
  const readLeadTime = useCallback(
    (skuId: string) =>
      readLeadTimeAvailability(allStockOutTimes?.leadTimeAvailability.get(skuId), warehouseId),
    [allStockOutTimes, warehouseId],
  )

  // One cache per warehouse + channel, so a fetch made for one scope never fills another's.
  const skuDetailCache = useMemo(() => new Map<string, SkuDataForVariant>(), [warehouseId, channel])
  const isCurrentCache = useEventCallback(
    (cache: Map<string, SkuDataForVariant>) => cache === skuDetailCache,
  )
  const [isExpandingAllSku, setIsExpandingAllSku] = useState(false)
  // Ref so detail grids (built once per open) don't capture a stale menu closure.
  const detailMenuItemsRef = useRef<() => SkuMenuItem[]>(() => [])

  // Called when a detail row opens; reads live grid state so columns are current.
  const detailCellRendererParams = useCallback(() => {
    return {
      detailGridOptions: {
        defaultColDef: defaultColumnDef,
        aggFuncs: PRODUCT_TABLE_AGG_FUNCS,
        // Anchor popups to body so async tooltip positioning can't dereference
        // a null grid div if the detail grid unmounts mid-show.
        popupParent: document.body,
        tooltipInteraction: true,
        tooltipShowDelay: 750,
        columnDefs: buildDetailColumnDefs(),
        context: gridRef.current?.api?.getGridOption("context"),
        getContextMenuItems: (params: GetContextMenuItemsParams) => [
          ...detailMenuItemsRef.current(),
          ...(params.defaultItems ?? []),
        ],
      },
      getDetailRowData: (params) => {
        const variant = params.data

        const cached = skuDetailCache.get(variant.variantId)
        if (cached) {
          params.successCallback(buildSkuDetailRows(variant, cached, warehouseId, readLeadTime))
          return
        }

        trpcUtils.client.sku.getSkuDataForVariant
          .query({
            variantId: variant.variantId,
            warehouseId,
            channels: channel ? [channel] : undefined,
          })
          .then((skuInfo) => {
            skuDetailCache.set(variant.variantId, skuInfo)
            params.successCallback(buildSkuDetailRows(variant, skuInfo, warehouseId, readLeadTime))
          })
          .catch((error) => {
            console.error("Error fetching SKU info:", error)
            params.successCallback([])
          })
      },
    } as IDetailCellRendererParams<IVariantSummary, any>
  }, [
    gridRef,
    buildDetailColumnDefs,
    defaultColumnDef,
    warehouseId,
    channel,
    readLeadTime,
    trpcUtils,
    skuDetailCache,
  ])

  const collapseAllMasterRows = useCallback(
    () => collapseMasterRows(gridRef.current?.api),
    [gridRef],
  )

  // Expand the given master rows (and any group rows above them) to SKU level, prefetching
  // their SKU data in one batched call. Works for the whole table or a single subtree.
  const expandNodesToSku = useCallback(
    async (masterNodes: IRowNode<IVariantSummary>[], groupNodes: IRowNode<IVariantSummary>[]) => {
      const api = gridRef.current?.api
      if (!api || isExpandingAllSku) return

      const variantIds = uniq(filterDefined(masterNodes.map((node) => node.data?.variantId)))
      if (variantIds.length === 0) return
      if (variantIds.length > EXPAND_ALL_SKU_MAX_ROWS) {
        toast.warn(
          `Too many rows to expand at once. Refine your filters (max ${EXPAND_ALL_SKU_MAX_ROWS}).`,
        )
        return
      }

      setIsExpandingAllSku(true)
      try {
        const missingVariantIds = variantIds.filter((id) => !skuDetailCache.has(id))
        if (missingVariantIds.length > 0) {
          const skuDataByVariant = await trpcUtils.client.sku.getSkuDataForVariants.mutate({
            variantIds: missingVariantIds,
            warehouseId,
            channels: channel ? [channel] : undefined,
          })
          if (!isCurrentCache(skuDetailCache)) return
          skuDataByVariant.forEach((skuInfo, variantId) => skuDetailCache.set(variantId, skuInfo))
        }
        // Batch: one rerender instead of per-node setExpanded, which corrupts the grid at scale.
        groupNodes.forEach((node) => {
          node.expanded = true
        })
        masterNodes.forEach((node) => {
          node.expanded = true
        })
        api.onGroupExpandedOrCollapsed()
      } catch (error) {
        console.error("Error expanding rows to SKU level:", error)
        toast.error("Failed to expand rows to SKU level")
      } finally {
        setIsExpandingAllSku(false)
      }
    },
    [gridRef, isExpandingAllSku, trpcUtils, warehouseId, channel, skuDetailCache, isCurrentCache],
  )

  const handleExpandAllToSkuLevel = useCallback(async () => {
    const api = gridRef.current?.api
    if (!api) return
    const masterNodes: IRowNode<IVariantSummary>[] = []
    const groupNodes: IRowNode<IVariantSummary>[] = []
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.group) groupNodes.push(node)
      if (node.master) masterNodes.push(node)
    })
    await expandNodesToSku(masterNodes, groupNodes)
    trackEvent("Product Table Expand All To Sku Toggled", {
      State: "Expanded",
      "Row Count": masterNodes.length,
    })
  }, [gridRef, expandNodesToSku])

  // Expand a single clicked row's subtree: a master row on its own, or a group row plus all
  // its descendant groups and variant rows.
  const handleExpandNodeToSku = useCallback(
    async (node: IRowNode<IVariantSummary>) => {
      const masterNodes: IRowNode<IVariantSummary>[] = []
      const groupNodes: IRowNode<IVariantSummary>[] = []
      const walk = (n: IRowNode<IVariantSummary>) => {
        if (n.group) {
          groupNodes.push(n)
          n.childrenAfterGroup?.forEach(walk)
        } else if (n.master) {
          masterNodes.push(n)
        }
      }
      walk(node)
      await expandNodesToSku(masterNodes, groupNodes)
      trackEvent("Product Table Row Expand Toggled", {
        State: "Expanded",
        "Row Count": masterNodes.length,
      })
    },
    [expandNodesToSku],
  )

  const collapseNode = useCallback(
    (node: IRowNode<IVariantSummary>) => {
      const api = gridRef.current?.api
      if (!api) return
      const walk = (n: IRowNode<IVariantSummary>) => {
        if (n.expanded) n.expanded = false
        n.childrenAfterGroup?.forEach(walk)
      }
      walk(node)
      api.onGroupExpandedOrCollapsed()
      trackEvent("Product Table Row Expand Toggled", { State: "Collapsed" })
    },
    [gridRef],
  )

  const handleCollapseAll = useCallback(() => {
    const api = gridRef.current?.api
    if (!api) return
    let anyCollapsed = false
    api.forEachNode((node) => {
      if (node.expanded && (node.master || node.group)) {
        node.expanded = false
        anyCollapsed = true
      }
    })
    if (anyCollapsed) api.onGroupExpandedOrCollapsed()
  }, [gridRef])

  const skuMenuItems = useCallback(
    (node?: IRowNode<IVariantSummary> | null): SkuMenuItem[] => {
      // Master-detail only: without it there are no SKU rows to expand into.
      if (!canExpandAllSku) return []
      const items: SkuMenuItem[] = []
      // For the clicked row (master or any aggregation group); absent inside a detail grid.
      if (node?.master || node?.group) {
        items.push(
          {
            name: "Expand row to SKU level",
            icon: menuIcon(<ArrowsOutSimple size={16} />),
            disabled: isExpandingAllSku,
            action: () => void handleExpandNodeToSku(node),
          },
          {
            name: "Collapse row",
            icon: menuIcon(<ArrowsIn size={16} />),
            action: () => collapseNode(node),
          },
        )
      }
      items.push(
        {
          name: "Expand all to SKU level",
          icon: menuIcon(<ArrowsOutSimple size={16} />),
          disabled: isExpandingAllSku,
          action: () => void handleExpandAllToSkuLevel(),
        },
        {
          name: "Collapse all",
          icon: menuIcon(<ArrowsIn size={16} />),
          action: () => {
            handleCollapseAll()
            trackEvent("Product Table Expand All To Sku Toggled", { State: "Collapsed" })
          },
        },
        "separator",
      )
      return items
    },
    [
      canExpandAllSku,
      isExpandingAllSku,
      handleExpandNodeToSku,
      collapseNode,
      handleExpandAllToSkuLevel,
      handleCollapseAll,
    ],
  )

  useEffect(() => {
    detailMenuItemsRef.current = skuMenuItems
  }, [skuMenuItems])

  useEffect(() => {
    if (!masterDetail) return
    collapseAllMasterRows()
  }, [warehouseId, channel, searchText, searchBy, localFilters, masterDetail])

  return { detailCellRendererParams, collapseAllMasterRows, skuMenuItems }
}

type SkuDataForVariant = RouterOutputs["sku"]["getSkuDataForVariant"]

function buildSkuDetailRows(
  variant: IVariantSummary,
  skuInfo: SkuDataForVariant,
  warehouseId: string | undefined,
  readLeadTime: (skuId: string) => ReturnType<typeof readLeadTimeAvailability>,
) {
  const skuDataById = new Map(variant.skuData.map((sku) => [sku.skuId, sku]))
  return variant.skuIds.map((skuId) => {
    const skuInf = skuInfo?.get(skuId)
    const skuSizeDimensions = skuDataById.get(skuId)
    const forecastsArray = skuInfo?.get(skuId)?.forecasts ?? []
    const outOfStockInformation = variant.outOfStockInformation?.find((o) => o.skuId === skuId)
    const stockOutInfoWarehouse = Object.fromEntries(
      Object.entries(variant.stockOutInfoWarehouse).map(([key, value]) => [
        key,
        value.filter((o) => o.skuId === skuId),
      ]),
    )

    const availability = readLeadTime(skuId)
    const suggestedOrder = Object.entries(variant.stockOutInfoWarehouse)
      .flatMap(([wId, entry]) => entry.map((e) => ({ warehouseId: wId, ...e })))
      .filter(
        (entry) =>
          entry.skuId === skuId && (warehouseId === undefined || warehouseId === entry.warehouseId),
      )
    const settings = { ...variant.settings }
    settings.bufferValue = skuInf?.bufferValue ?? variant.settings.bufferValue
    settings.bufferType = skuInf?.bufferType ?? variant.settings.bufferType
    settings.activatedFrom = skuInf?.activatedFrom ?? variant.settings.activatedFrom

    const stockLevelDetails = skuInfo?.get(skuId)?.stockLevelDetails ?? []
    const skuLevelStorageLocation = skuInfo?.get(skuId)?.skuLocationData ?? []

    const storageLocationData = filterDefined(
      (variant.storageLocationData ?? []).map((s) => {
        const locationData = s?.skuData?.find((sd) => sd.skuId === skuId)
        if (!locationData) {
          return undefined
        }

        return {
          locationId: s.locationId,
          stockLevel: locationData.quantity ?? 0,
          thirteenWeeksForecast: getForecastsForWeeks(forecastsArray, 13) * locationData.weight,
          nextYearsForecast: getForecastsForWeeks(forecastsArray, 52) * locationData.weight,
          lastYearsSales:
            skuLevelStorageLocation.find((sl) => sl.locationId === s.locationId)?.lastYearsSales ??
            0,
          minimumStockLevel: locationData.minimumStockLevel ?? 0,
          skuData: [locationData],
          availability: locationData.quantity > 0 ? 1 : 0,
        }
      }),
    )
    return {
      skuId,
      skuIds: [skuId],
      variantId: variant.variantId,
      vendors: variant.vendors,
      collections: variant.collections,
      variantForecast: forecastsArray,
      size: skuInfo?.get(skuId)?.size ?? "",
      sizeDimension1: skuSizeDimensions?.sizeDimension1 ?? null,
      sizeDimension2: skuSizeDimensions?.sizeDimension2 ?? null,
      costPrice: skuInf?.costPrice ?? 0,
      // null (not 0) so the column renders blank when absent/null (pre-ingestion or no data).
      recommendedRetailPrice: skuInf?.recommendedRetailPrice ?? null,
      wholesalePrice: skuInf?.wholesalePrice ?? null,
      storageLocationData: storageLocationData,
      variantName: variant.variantName || variant.color,
      thirteenWeeksForecast: getForecastsForWeeks(forecastsArray, 13),
      forecastQuantity: getForecastsForWeeks(forecastsArray, 52),
      salesQuantity: skuInf?.lastYearSales ?? 0,
      revenue: skuInf?.lastYearRevenue,
      preOrderQuantity: skuInf?.totalPreOrders,
      soldSince: skuInf?.firstSaleDate,
      soldAndActiveSince: getSoldAndActiveSince(
        skuInf?.firstSaleDate,
        variant.settings.activatedFrom,
      ),
      last30DaysSales: skuInf?.last30DaysSales ?? 0,
      lastYearReturns: skuInf?.lastYearReturns ?? 0,
      lastTwoYearSales: skuInf?.lastTwoYearSales ?? 0,
      totalSales: skuInf?.totalSales ?? 0,
      lastYearPreOrders: skuInf?.lastYearPreOrders,
      availableToSell: skuInf?.availableToSell ?? null,
      futureKnownPreorders: variant.futureKnownPreorders?.filter((po) => po.skuId === skuId) ?? [],

      incomingOrders: skuInfo?.get(skuId)?.incomingOrders ?? [],
      incomingStock: skuInfo?.get(skuId)?.incomingStock ?? 0,
      incomingOrdersWarehouse: skuInfo?.get(skuId)?.incomingOrdersWarehouse ?? [],

      stockOutInfoWarehouse: stockOutInfoWarehouse,
      stockLevel: skuInfo?.get(skuId)?.stockLevel ?? 0,
      stockLevelDetails: stockLevelDetails,
      reservedStockLevel: sumBy(stockLevelDetails, "reserved"),
      // Raw pair plus the window it covers; the weekly rate is derived at display time.
      availabilityWithinLeadTime: availability,
      lostSalesWithinLeadTime: availability.lostSales,

      stockStatus: outOfStockInformation?.status ?? "",
      suggestedOrder: sumBy(suggestedOrder, "suggestedOrder"),
      outOfStockInformation: [outOfStockInformation],

      settings: settings,
      productType: variant.settings.productType,
      userId: variant.settings.userId,
      groupVendor: variant.groupVendor,
      groupCollection: variant.collections[0]?.name,
      extraData: skuInf?.extraData ?? variant.extraData,
      sizeSplitWeight: skuInf?.weight ?? null,
      inventoryTurnCogs: skuInf?.inventoryTurnCogs ?? 0,
      inventoryTurnAvgInvValue: skuInf?.inventoryTurnAvgInvValue ?? 0,
      inventoryTurnStartDate: skuInf?.inventoryTurnStartDate ?? null,
      skuStockoutImputedFraction: skuInf?.skuStockoutImputedFraction ?? null,
    }
  })
}
